import { useState, memo } from 'react';
import { PH } from '../components/shared';
import { useApp } from '../context/AppContext';
import * as api from '../services/api';

const ARTICLES = [
  {
    ico: "👤", title: "How to set up your profile", time: "3 min read", tag: "Getting Started",
    steps: [
      { title: "Go to Team → Users", desc: "Navigate to the Team section in the sidebar and click on Users to see all team members." },
      { title: "Select your profile", desc: "Click on your name in the members list or click 'Edit' next to your profile." },
      { title: "Fill in your details", desc: "Add your name, job title, company, bio, and upload a professional photo. All fields are optional but help you make a great first impression." },
      { title: "Add your social links", desc: "Scroll down to the links section. Add your LinkedIn, website, email, phone, and any other platforms you want to share." },
      { title: "Save & share", desc: "Click Save. Your digital profile is now live! Share it via your NFC card or QR code." },
    ],
    tip: "Keep your profile concise — name, title, and 3-5 key links work best for quick networking.",
  },
  {
    ico: "💳", title: "How to share your Gotaps card", time: "2 min read", tag: "Cards",
    steps: [
      { title: "Activate your NFC card", desc: "If you have a physical NFC card, go to NFC settings and link it to your profile using the serial number on the back." },
      { title: "Tap to share", desc: "Hold your NFC card near any smartphone (iPhone or Android). The recipient's phone will automatically open your digital profile — no app needed." },
      { title: "Use your QR code", desc: "Don't have your card? Open your profile and tap the QR icon. Show the QR code on screen or download it to share digitally." },
      { title: "Share via link", desc: "Every profile has a unique URL. Copy it and share via email, WhatsApp, SMS, or any messaging app." },
    ],
    tip: "NFC taps work best when you hold the card steady for 1-2 seconds near the top of the recipient's phone.",
  },
  {
    ico: "🎯", title: "How to capture leads", time: "4 min read", tag: "Leads",
    steps: [
      { title: "Share your card or QR", desc: "Every time someone taps your NFC card, scans your QR code, or visits your profile link, they become a potential lead." },
      { title: "Lead capture form", desc: "When visitors view your profile, they can choose to share their contact info with you through the built-in lead capture form." },
      { title: "View your leads", desc: "Go to Leads → All Leads to see everyone who has interacted with your profile. Each lead shows their name, email, company, and how they found you." },
      { title: "AI lead scoring", desc: "Our AI automatically scores each lead from 1-100 based on engagement level, company size, and interaction patterns. Focus on high-scoring leads first." },
      { title: "Follow up", desc: "Use the notes field to track conversations. Export leads to your CRM, or use our email campaigns to follow up automatically." },
    ],
    tip: "Leads captured via NFC tap convert 3.2x faster than other methods. Always carry your card!",
  },
  {
    ico: "👥", title: "How to manage team members", time: "5 min read", tag: "Team",
    steps: [
      { title: "Navigate to Team → Users", desc: "This is your central hub for managing all team members and their digital profiles." },
      { title: "Add a new member", desc: "Click 'Create User' to add someone. Fill in their name, email, and role. They'll receive an invitation email to set up their profile." },
      { title: "Organize with subteams", desc: "Go to Team → Subteams to create groups (e.g., Sales, Marketing). Assign members to subteams for better organization and reporting." },
      { title: "Manage permissions", desc: "Each member can have their links, profile visibility, and direct mode configured independently by the admin." },
      { title: "Track performance", desc: "View each member's analytics — total taps, leads captured, and engagement rates — from their profile page." },
    ],
    tip: "Use subteams to run targeted campaigns. For example, send a campaign only to your Sales team for a product launch.",
  },
  {
    ico: "📊", title: "How to export analytics", time: "3 min read", tag: "Analytics",
    steps: [
      { title: "Go to Analytics & Reports", desc: "Click 'Analytics & Reports' in the sidebar to access your comprehensive dashboard." },
      { title: "Choose your date range", desc: "Use the date picker to select the period you want to analyze. You can view daily, weekly, monthly, or custom ranges." },
      { title: "Review key metrics", desc: "See total taps, leads captured, conversion rates, top performers, and engagement trends at a glance." },
      { title: "Export your data", desc: "Click the export button to download your data as CSV or PDF. Choose which metrics to include in your report." },
    ],
    tip: "Schedule weekly exports to keep your CRM in sync. Check the 'Top Performers' section to identify and reward your best networkers.",
  },
  {
    ico: "🔔", title: "How to set up notifications", time: "2 min read", tag: "Settings",
    steps: [
      { title: "Go to Settings → Notifications", desc: "Access notification preferences from the Settings section in the sidebar." },
      { title: "Choose what to track", desc: "Enable or disable notifications for: new leads captured, NFC taps, campaign milestones, team activity, and billing alerts." },
      { title: "Set delivery channels", desc: "Choose how you want to be notified: in-app notifications, email alerts, or both." },
      { title: "Configure team alerts", desc: "As an admin, you can set up alerts for when team members hit milestones or when new leads come in from specific campaigns." },
    ],
    tip: "Start with email alerts for new leads only — you can always add more notification types later without getting overwhelmed.",
  },
];

/* ── Article Detail (inline page, not modal) ── */
const ArticleDetail = memo(({ article, onBack }) => {
  const card = { background: "rgba(255,255,255,.72)", border: "1px solid rgba(255,255,255,.9)", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)" };
  return (
    <div className="pg fi">
      <button className="btn bs" style={{ padding: "7px 14px", fontSize: 12, gap: 6, marginBottom: 20 }} onClick={onBack}>← Back to Help Center</button>

      <div style={{ maxWidth: 700 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <div style={{ width: 52, height: 52, borderRadius: 15, background: "rgba(100,116,139,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{article.ico}</div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 720, color: "#0f172a", lineHeight: 1.3, fontFamily: "var(--fd)" }}>{article.title}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5 }}>
              <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{article.time}</span>
              <span style={{ fontSize: 10.5, fontWeight: 660, padding: "3px 10px", borderRadius: 20, background: "rgba(100,116,139,.08)", color: "#64748b" }}>{article.tag}</span>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div style={{ ...card, padding: "28px 32px", marginBottom: 16 }}>
          {article.steps.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 16, marginBottom: i < article.steps.length - 1 ? 24 : 0, position: "relative" }}>
              {/* Connector line */}
              {i < article.steps.length - 1 && (
                <div style={{ position: "absolute", left: 15, top: 32, bottom: -12, width: 2, background: "rgba(0,0,0,.06)" }} />
              )}
              <div style={{ width: 32, height: 32, borderRadius: 10, background: "linear-gradient(150deg,#475569,#0f172a)", color: "#fff", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative", zIndex: 1 }}>{i + 1}</div>
              <div style={{ flex: 1, paddingTop: 2 }}>
                <div style={{ fontSize: 14.5, fontWeight: 670, color: "#0f172a", marginBottom: 5 }}>{step.title}</div>
                <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.7 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pro Tip */}
        {article.tip && (
          <div style={{ padding: "16px 20px", background: "rgba(8,145,178,.06)", border: "1px solid rgba(8,145,178,.14)", borderRadius: 14, display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 20 }}>
            <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>💡</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0891b2", marginBottom: 4 }}>Pro Tip</div>
              <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.65 }}>{article.tip}</div>
            </div>
          </div>
        )}

        {/* Helpful */}
        <div style={{ ...card, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 520 }}>Was this article helpful?</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ padding: "7px 16px", borderRadius: 9, border: "1px solid rgba(0,0,0,.08)", background: "rgba(255,255,255,.8)", cursor: "pointer", fontSize: 12.5, fontFamily: "var(--f)", color: "#475569", fontWeight: 560, transition: "all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(5,150,105,.08)"; e.currentTarget.style.borderColor = "rgba(5,150,105,.2)"; e.currentTarget.style.color = "#059669"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.8)"; e.currentTarget.style.borderColor = "rgba(0,0,0,.08)"; e.currentTarget.style.color = "#475569"; }}>
              👍 Yes
            </button>
            <button style={{ padding: "7px 16px", borderRadius: 9, border: "1px solid rgba(0,0,0,.08)", background: "rgba(255,255,255,.8)", cursor: "pointer", fontSize: 12.5, fontFamily: "var(--f)", color: "#475569", fontWeight: 560, transition: "all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(220,38,38,.06)"; e.currentTarget.style.borderColor = "rgba(220,38,38,.18)"; e.currentTarget.style.color = "#dc2626"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.8)"; e.currentTarget.style.borderColor = "rgba(0,0,0,.08)"; e.currentTarget.style.color = "#475569"; }}>
              👎 No
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

/* ── Coming Soon block ── */
const ComingSoonBlock = ({ icon, title, subtitle }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center" }}>
    <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(100,116,139,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, marginBottom: 14 }}>{icon}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--t1)", marginBottom: 6, fontFamily: "var(--fd)" }}>Coming Soon</div>
    <div style={{ fontSize: 12.5, color: "var(--t3)", lineHeight: 1.6, maxWidth: 300 }}>{subtitle}</div>
  </div>
);

/* ── Main Support Page ── */
export const PageSupport = memo(() => {
  const { showToast } = useApp();
  const [tab, setTab] = useState("help");
  const [faqOpen, setFaqOpen] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Contact form state
  const [contactForm, setContactForm] = useState({ subject: "", priority: "medium", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  // If viewing an article, show the detail page
  if (selectedArticle) {
    return <ArticleDetail article={selectedArticle} onBack={() => setSelectedArticle(null)} />;
  }

  const TABS = [
    { id: "help", ico: "📚", lbl: "Help Center" },
    { id: "contact", ico: "✉️", lbl: "Contact Support" },
    { id: "chat", ico: "💬", lbl: "Live Chat" },
    { id: "ticket", ico: "🎫", lbl: "Submit Ticket" },
    { id: "videos", ico: "🎬", lbl: "Video Tutorials" },
    { id: "faq", ico: "❓", lbl: "FAQ" },
  ];

  const VIDEOS = [
    { title: "Getting Started with Gotaps", dur: "3:42", views: "12.4k", c: "#0891b2" },
    { title: "Setting Up Your NFC Card", dur: "5:18", views: "8.7k", c: "#7c3aed" },
    { title: "AI Lead Scoring Explained", dur: "4:55", views: "6.2k", c: "#059669" },
    { title: "Managing Your Team", dur: "6:10", views: "5.1k", c: "#d97706" },
    { title: "Running Email Campaigns", dur: "4:28", views: "4.8k", c: "#dc2626" },
    { title: "Analytics & Reporting Deep Dive", dur: "7:33", views: "3.9k", c: "#475569" },
  ];

  const FAQS = [
    { q: "How do I activate my NFC device?", a: "Go to Team → Members, select a member, then click 'Activate Device'. You'll need their card serial number found on the back of the NFC card." },
    { q: "How do I share my QR code?", a: "Open your profile, tap the QR icon in the top-right corner. You can download it as PNG or share directly via link." },
    { q: "How do I connect social links?", a: "Go to Settings → Account, scroll to 'Social Links'. Add your profiles for LinkedIn, Twitter, Instagram, and others." },
    { q: "How do I manage my leads?", a: "Navigate to Leads → All Leads. You can filter by status, assign to team members, export as CSV, or trigger AI enrichment for any lead." },
    { q: "Can I export my data?", a: "Yes. Go to Settings → Data & Privacy → Export Data. You'll receive a CSV export of all leads, analytics, and team data within minutes." },
    { q: "What NFC cards are compatible?", a: "Gotaps works with all standard NFC cards (NTAG213, NTAG215, NTAG216). Our own branded cards are pre-configured and ready to use out of the box." },
  ];

  const handleContactSubmit = async () => {
    if (!contactForm.subject.trim()) { showToast("Please enter a subject.", "error"); return; }
    if (!contactForm.message.trim()) { showToast("Please enter a message.", "error"); return; }
    setContactLoading(true);
    try {
      await api.submitSupportInquiry(contactForm);
      setContactSent(true);
      setContactForm({ subject: "", priority: "medium", message: "" });
    } catch (e) {
      showToast(e.message || "Failed to send message.", "error");
    } finally { setContactLoading(false); }
  };

  const card = { background: "rgba(255,255,255,.72)", border: "1px solid rgba(255,255,255,.9)", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)" };

  return (
    <div className="pg fi">
      <PH eyebrow="Help & Support" title="Support Center" sub="Find answers, contact us, or start a live chat" />
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

        {/* ── Vertical sidebar nav ── */}
        <div style={{ width: 210, flexShrink: 0, display: "flex", flexDirection: "column", gap: 4, position: "sticky", top: 80 }}>
          {TABS.map(({ id, ico, lbl }) => {
            const active = tab === id;
            return (
              <button key={id} onClick={() => setTab(id)} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", borderRadius: 11, border: "none",
                cursor: "pointer", fontFamily: "var(--f)",
                background: active ? "rgba(15,23,42,.92)" : "rgba(255,255,255,.65)",
                color: active ? "#fff" : "#64748b",
                fontSize: 13, fontWeight: active ? 660 : 480,
                boxShadow: active ? "0 4px 14px rgba(0,0,0,.18)" : "0 2px 8px rgba(0,0,0,.04)",
                borderLeft: active ? "3px solid #0891b2" : "3px solid transparent",
                transition: "all .16s", textAlign: "left", width: "100%",
              }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,.9)"; e.currentTarget.style.color = "#0f172a"; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,.65)"; e.currentTarget.style.color = "#64748b"; } }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{ico}</span>
                <span>{lbl}</span>
              </button>
            );
          })}
          <div style={{ ...card, padding: "14px 16px", marginTop: 12, background: "rgba(8,145,178,.06)", border: "1px solid rgba(8,145,178,.16)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0891b2", marginBottom: 5 }}>Need urgent help?</div>
            <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.55, marginBottom: 10 }}>Our team is online and ready to assist you right now.</div>
            <button className="btn bp" style={{ width: "100%", justifyContent: "center", fontSize: 11.5, padding: "7px 0" }} onClick={() => setTab("chat")}>💬 Start Live Chat</button>
          </div>
        </div>

        {/* ── Main content ── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* HELP CENTER */}
          {tab === "help" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 13 }}>
              {ARTICLES.map((a, i) => (
                <div key={i} onClick={() => setSelectedArticle(a)} style={{ ...card, padding: "18px 20px", cursor: "pointer", transition: "all .18s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,.1),inset 0 1px 0 rgba(255,255,255,.95)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)"; }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 11, background: "rgba(100,116,139,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{a.ico}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 680, color: "#0f172a", marginBottom: 6, lineHeight: 1.3 }}>{a.title}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 10.5, color: "#94a3b8" }}>{a.time}</span>
                        <span style={{ fontSize: 10, fontWeight: 660, padding: "2px 8px", borderRadius: 20, background: "rgba(100,116,139,.08)", color: "#64748b" }}>{a.tag}</span>
                      </div>
                    </div>
                    <span style={{ fontSize: 14, color: "#94a3b8" }}>›</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CONTACT SUPPORT */}
          {tab === "contact" && (
            <div style={{ maxWidth: 560 }}>
              {contactSent ? (
                <div style={{ ...card, padding: "40px 30px", textAlign: "center" }}>
                  <div style={{ width: 64, height: 64, borderRadius: 18, background: "rgba(5,150,105,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 16px" }}>✅</div>
                  <div style={{ fontSize: 18, fontWeight: 720, color: "#0f172a", marginBottom: 8, fontFamily: "var(--fd)" }}>Thank You!</div>
                  <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.7, marginBottom: 20 }}>
                    Your message has been sent successfully.<br />We will contact you via email shortly.
                  </div>
                  <button className="btn bs" style={{ fontSize: 12 }} onClick={() => setContactSent(false)}>Send Another Message</button>
                </div>
              ) : (
                <div style={{ ...card, padding: "28px 30px" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 20 }}>Send us a message</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 650, color: "#475569", display: "block", marginBottom: 5 }}>Subject</label>
                      <input className="inp" placeholder="Describe your issue briefly" value={contactForm.subject} onChange={e => setContactForm(f => ({ ...f, subject: e.target.value }))} style={{ width: "100%", boxSizing: "border-box" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 650, color: "#475569", display: "block", marginBottom: 5 }}>Priority</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        {["low", "medium", "high", "urgent"].map(p => (
                          <button key={p} onClick={() => setContactForm(f => ({ ...f, priority: p }))} style={{ flex: 1, padding: "8px 4px", borderRadius: 9, border: "1px solid rgba(0,0,0,.08)", background: contactForm.priority === p ? "#0f172a" : "rgba(255,255,255,.8)", color: contactForm.priority === p ? "#fff" : "#64748b", fontSize: 11.5, fontWeight: contactForm.priority === p ? 680 : 500, cursor: "pointer", fontFamily: "var(--f)", textTransform: "capitalize" }}>{p}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 650, color: "#475569", display: "block", marginBottom: 5 }}>Message</label>
                      <textarea className="inp" placeholder="Please describe your issue in detail..." rows={5} value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} style={{ width: "100%", resize: "vertical", boxSizing: "border-box", fontFamily: "var(--f)" }} />
                    </div>
                    <button className="btn bp" style={{ alignSelf: "flex-start", gap: 8 }} disabled={contactLoading} onClick={handleContactSubmit}>
                      {contactLoading ? "Sending..." : "✉️ Send Message"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LIVE CHAT */}
          {tab === "chat" && <ComingSoonBlock icon="💬" title="Live Chat" subtitle="Real-time chat support will be available here soon. Use Contact Support to reach us in the meantime." />}

          {/* SUBMIT TICKET */}
          {tab === "ticket" && <ComingSoonBlock icon="🎫" title="Submit Ticket" subtitle="Ticket tracking system is coming soon. Use Contact Support to send us your issue." />}

          {/* VIDEO TUTORIALS */}
          {tab === "videos" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 13 }}>
              {VIDEOS.map((v, i) => (
                <div key={i} style={{ ...card, overflow: "hidden", cursor: "pointer", transition: "all .18s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,.1),inset 0 1px 0 rgba(255,255,255,.95)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)"; }}>
                  <div style={{ height: 120, background: `linear-gradient(135deg,${v.c},${v.c}88)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    <div style={{ width: 42, height: 42, borderRadius: "50%", background: "rgba(255,255,255,.22)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, backdropFilter: "blur(8px)" }}>▶</div>
                    <div style={{ position: "absolute", bottom: 7, right: 9, fontSize: 10.5, fontWeight: 660, color: "rgba(255,255,255,.9)", background: "rgba(0,0,0,.3)", padding: "2px 7px", borderRadius: 6 }}>{v.dur}</div>
                  </div>
                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 660, color: "#0f172a", marginBottom: 5, lineHeight: 1.35 }}>{v.title}</div>
                    <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{v.views} views</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* FAQ */}
          {tab === "faq" && (
            <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 9 }}>
              {FAQS.map((f, i) => (
                <div key={i} style={{ ...card, overflow: "hidden", cursor: "pointer", transition: "all .18s" }} onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                  <div style={{ padding: "15px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 640, color: "#0f172a" }}>{f.q}</div>
                    <span style={{ fontSize: 18, color: "#94a3b8", transition: "transform .2s", transform: faqOpen === i ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}>›</span>
                  </div>
                  {faqOpen === i && <div style={{ padding: "0 20px 15px", fontSize: 13, color: "#475569", lineHeight: 1.7, borderTop: "1px solid rgba(0,0,0,.05)" }}>{f.a}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
